/**
 * ResearchModalShell
 *
 * Shared container for all Research Modals. Provides the standard:
 *  - fixed full-screen overlay (z-50)
 *  - brand-violet sticky header with title + step label
 *  - disabled-close support during async operations
 *  - scrollable body slot via children
 *
 * All 8 Research Modals (Company, Product, People, Goal, ALO, Story, BrandVision, Target)
 * use this instead of rolling their own backdrop + header.
 */

import { X } from 'lucide-react'
import { FC, ReactNode } from 'react'

interface ResearchModalShellProps {
    isOpen: boolean
    onClose: () => void
    title: string
    stepLabel: string
    /** Pass true while async operations (scraping, saving) are in progress to disable X close */
    disableClose?: boolean
    children: ReactNode
}

export const ResearchModalShell: FC<ResearchModalShellProps> = ({
    isOpen,
    onClose,
    title,
    stepLabel,
    disableClose = false,
    children,
}) => {
    if (!isOpen) return null

    return (
        <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto'>
            <div className='bg-gray-50 rounded-xl shadow-2xl max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto'>
                {/* Sticky header */}
                <div className='bg-gradient-to-r from-[#481C4F] to-[#5D3A74] text-white p-6 rounded-t-xl flex items-center justify-between sticky top-0 z-10'>
                    <div>
                        <h2 className='text-2xl font-bold'>{title}</h2>
                        <p className='text-sm text-white/80 mt-1'>{stepLabel}</p>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={disableClose}
                        className='hover:bg-white/20 p-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
                        aria-label='Close'
                    >
                        <X className='w-5 h-5' />
                    </button>
                </div>

                {/* Body */}
                <div className='p-6'>{children}</div>
            </div>
        </div>
    )
}
